import { useEffect, useState } from 'react'
import useApi from '../api'
import { useParams, useNavigate } from 'react-router-dom'
import Pages from './Pages'
import { toast } from 'react-toastify'
import toastConfig from '../config/Toast'
import 'react-toastify/dist/ReactToastify.css'
import { formatDate } from '../utils/dateFormatter'
import useAnalytics from '../analytics/analytics'
import { useUser } from '../user/UserContext'
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion"
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import KnowledgeNavBar from '../KnowledgeNavBar'
import FailedPagesDialog from './FailedPagesDialog'
import TrainPagesDialog from './TrainPagesDialog'
import WebsiteSettings from './WebsiteSettings'
import { Settings } from 'lucide-react'

interface WebsiteData {
    id: string;
    url: string;
    base_url: string;
    title: string;
    meta_description: string;
    meta_keywords: string;
    language: string;
    created_at: string;
    updated_at: string;
    indexed: boolean;
    index_status: string;
    indexed_at: string;
    settings?: {
        autotrain: boolean;
        routeMode: 'include' | 'exclude';
        routes: string[];
    };
}

export default function Website() {
    const { id } = useParams<{ id: string }>();
    const { get, post } = useApi();
    const [website, setWebsite] = useState<WebsiteData | undefined>();
    const { captureEvent } = useAnalytics();
    const { checkLicenseType } = useUser();
    const [urlsToIndex, setUrlsToIndex] = useState('');
    const navigate = useNavigate();
    const [failedPagesDialog, setFailedPagesDialog] = useState<{ open: boolean, websiteId: string, pages: any[] }>({
        open: false,
        websiteId: '',
        pages: []
    });
    const [failedPagesCount, setFailedPagesCount] = useState(0);
    const [trainPagesDialog, setTrainPagesDialog] = useState<{ open: boolean, websiteId: string, pages: any[] }>({
        open: false,
        websiteId: '',
        pages: []
    });
    const [notTrainedPagesCount, setNotTrainedPagesCount] = useState(0);
    const [settingsDialog, setSettingsDialog] = useState(false);

    useEffect(() => {
        if (id) {
            get(`/websites/${id}`).then((response) => {
                setWebsite(response.data);
            });
            
            // Fetch failed pages count
            const failedParams = new URLSearchParams({
                website_id: id,
                index_status: 'Failed',
                per_page: '1'
            });
            get(`/pages/?${failedParams.toString()}`).then((response) => {
                setFailedPagesCount(response.data.total || 0);
            }).catch((error) => {
                console.error('Error fetching failed pages count:', error);
            });

            // Fetch not trained pages count
            const notTrainedParams = new URLSearchParams({
                website_id: id,
                index_status: 'Not Trained',
                per_page: '1'
            });
            get(`/pages/?${notTrainedParams.toString()}`).then((response) => {
                setNotTrainedPagesCount(response.data.total || 0);
            }).catch((error) => {
                console.error('Error fetching not trained pages count:', error);
            });
            
            captureEvent('viewed_website', {
                website_id: id,
            });
        }
    }, [id]);

    const handleTabChange = (newTab: string) => {
        navigate(`/knowledge?tab=${newTab}`);
    };

    const handleReindex = async () => {
        try {
            await post('/re-index-pages/', { website_id: id });
            toast.success('Training started!', toastConfig);
            captureEvent('reindexed_pages', {
                website_id: id,
            });
        } catch (error: any) {
            toast.error(error.detail || 'Error training pages.', toastConfig);
            console.error('Training error:', error);
        }
    };

    const handleRecrawl = () => {
        if (!checkLicenseType('pro')) {
            toast.error('Upgrade to the Engage plan to crawl your website.', toastConfig);
            return;
        }
        try {
            post('/recrawl-website/', { website_id: id }).then((res) => {
                toast.success('Crawling in progress', toastConfig);
                captureEvent('crawl_website', {
                    website_id: website,
                });
            }).catch((error) => {
                console.error('Error crawling website:', error);
                toast.error('Error starting crawl.', toastConfig);
            });
        } catch (e) {
            toast.error('Invalid website URL', toastConfig);
        }
    };

    const handleUrlSubmit = () => {
        const urls = urlsToIndex.split(/[\s,]+/).filter(url => url.trim() !== '');
        if (urls.length === 0) {
            toast.error('Please enter at least one valid URL', toastConfig);
            return;
        }

        post('/add-urls-to-index/', { website_id: id, urls: urls })
            .then((res) => {
                toast.success('URLs submitted for training', toastConfig);
                setUrlsToIndex('');
                captureEvent('submitted_urls_for_indexing', {
                    website_id: id,
                    url_count: urls.length,
                });
            })
            .catch((error) => {
                console.error('Error submitting URLs:', error);
                toast.error('Error submitting URLs for training.', toastConfig);
            });
    };

    const fetchFailedPages = async () => {
        if (!id) return;
        
        try {
            const params = new URLSearchParams({
                website_id: id,
                index_status: 'Failed',
                per_page: '100'
            });

            const response = await get(`/pages/?${params.toString()}`);
            const failedPages = (response.data.items || []).filter((page: any) => page.index_status === 'Failed');

            setFailedPagesDialog({
                open: true,
                websiteId: id,
                pages: failedPages
            });

        } catch (error) {
            console.error('Error fetching failed pages:', error);
            toast.error('Failed to load failed pages', toastConfig);
        }
    };

    const closeFailedPagesDialog = () => {
        setFailedPagesDialog({
            open: false,
            websiteId: '',
            pages: []
        });
    };

    const handleFailedPagesSuccess = () => {
        // Refresh failed pages count after successful retraining
        if (id) {
            const params = new URLSearchParams({
                website_id: id,
                index_status: 'Failed',
                per_page: '1'
            });
            get(`/pages/?${params.toString()}`).then((response) => {
                setFailedPagesCount(response.data.total || 0);
            }).catch((error) => {
                console.error('Error fetching failed pages count:', error);
            });
        }
    };

    const fetchNotTrainedPages = async () => {
        if (!id) return;
        
        try {
            const params = new URLSearchParams({
                website_id: id,
                index_status: 'Not Trained',
                per_page: '100'
            });

            const response = await get(`/pages/?${params.toString()}`);
            const notTrainedPages = (response.data.items || []).filter((page: any) => page.index_status === 'Not Trained');

            setTrainPagesDialog({
                open: true,
                websiteId: id,
                pages: notTrainedPages
            });

        } catch (error) {
            console.error('Error fetching not trained pages:', error);
            toast.error('Failed to load not trained pages', toastConfig);
        }
    };

    const closeTrainPagesDialog = () => {
        setTrainPagesDialog({
            open: false,
            websiteId: '',
            pages: []
        });
    };

    const handleTrainPagesSuccess = () => {
        // Refresh not trained pages count after successful training
        if (id) {
            const params = new URLSearchParams({
                website_id: id,
                index_status: 'Not Trained',
                per_page: '1'
            });
            get(`/pages/?${params.toString()}`).then((response) => {
                setNotTrainedPagesCount(response.data.total || 0);
            }).catch((error) => {
                console.error('Error fetching not trained pages count:', error);
            });
        }
    };

    return (
        <div className="flex h-screen w-full overflow-hidden">
            <KnowledgeNavBar activeTab="websites" handleTabChange={handleTabChange} />
            <div className="flex-1 h-full overflow-y-auto scrollbar-hidden p-6">
                {website && (
                    <>
                        <Card className="mb-6">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-3xl font-bold">{website.url}</CardTitle>
                                    {(checkLicenseType('pro') || checkLicenseType('business') || checkLicenseType('enterprise') || checkLicenseType('aimdoc')) && (
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={() => setSettingsDialog(true)}
                                            className="flex items-center"
                                        >
                                            <Settings className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="flex gap-4 mb-6">
                                    <Button variant="outline" onClick={handleReindex}>
                                        Train Pages
                                    </Button>
                                    {failedPagesCount > 0 && (
                                        <Button variant="outline" onClick={fetchFailedPages}>
                                            Retry Failed Pages ({failedPagesCount})
                                        </Button>
                                    )}
                                    {notTrainedPagesCount > 0 && (
                                        <Button variant="outline" onClick={fetchNotTrainedPages}>
                                            Train New Pages ({notTrainedPagesCount})
                                        </Button>
                                    )}
                                    <Button variant="outline" onClick={handleRecrawl}>
                                        Crawl Site
                                    </Button>
                                </div>

                                <Accordion type="single" collapsible>
                                    <AccordionItem value="website-details">
                                        <AccordionTrigger>Website Details</AccordionTrigger>
                                        <AccordionContent>
                                            <Table>
                                                <TableBody>
                                                    {[
                                                        { label: 'URL', value: website.url },
                                                        { label: 'Base URL', value: website.base_url },
                                                        { label: 'Title', value: website.title },
                                                        { label: 'Meta Description', value: website.meta_description },
                                                        { label: 'Meta Keywords', value: website.meta_keywords },
                                                        { label: 'Language', value: website.language },
                                                        { label: 'Created At', value: formatDate(website.created_at) },
                                                        { label: 'Updated At', value: formatDate(website.updated_at) },
                                                        { label: 'Trained', value: website.indexed ? 'Yes' : 'No' },
                                                        { label: 'Training Status', value: website.index_status },
                                                        { label: 'Trained At', value: website.indexed_at ? formatDate(website.indexed_at) : 'N/A' }
                                                    ].filter(item => item.value)
                                                      .map((item, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell className="font-medium">{item.label}</TableCell>
                                                            <TableCell>{item.value}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>

                                <div className="mt-6 space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="urlsToIndex">Add URLs to Train</Label>
                                        <Textarea
                                            id="urlsToIndex"
                                            value={urlsToIndex}
                                            onChange={(e) => setUrlsToIndex(e.target.value)}
                                            placeholder="Enter space or comma separated URLs to train"
                                            className="h-40"
                                        />
                                        <Button onClick={handleUrlSubmit} className="w-full">
                                            Submit URLs for Training
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Page History</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {id && <Pages websiteId={id} />}
                            </CardContent>
                        </Card>
                    </>
                )}
                
                {/* Failed Pages Dialog */}
                <FailedPagesDialog
                    open={failedPagesDialog.open}
                    websiteId={failedPagesDialog.websiteId}
                    pages={failedPagesDialog.pages}
                    onClose={closeFailedPagesDialog}
                    onSuccess={handleFailedPagesSuccess}
                />
                
                {/* Train Pages Dialog */}
                <TrainPagesDialog
                    open={trainPagesDialog.open}
                    websiteId={trainPagesDialog.websiteId}
                    pages={trainPagesDialog.pages}
                    onClose={closeTrainPagesDialog}
                    onSuccess={handleTrainPagesSuccess}
                />

                {/* Website Settings Dialog */}
                <WebsiteSettings
                    open={settingsDialog}
                    websiteId={id || ''}
                    onClose={() => setSettingsDialog(false)}
                    onSuccess={() => {
                        // Could refresh website data here if needed
                    }}
                />
            </div>
        </div>
    )
}